import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CondominioDisponible } from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import { Boton, Cargando, ErrorCaja, Vacio } from '../componentes/ui';
import { ErrorApi } from '../lib/api';
import { useRecurso } from '../lib/consultas';
import { useSesion } from '../lib/sesion';

/**
 * Elegir en qué fraccionamiento se trabaja.
 *
 * ## Para quién existe
 *
 * Para el SUPER_ADMIN de Kodica, que **no pertenece a ningún condominio**. Esa
 * no es una carencia: en un servidor que atiende varios, elegirle uno por él
 * sería peor que no dejarle entrar, porque acabaría operando sobre el padrón
 * equivocado sin haberlo pedido. Por eso `exigirCondominio` lo rechaza, y por
 * eso hace falta esta pantalla: sin ella, quien opera la plataforma no puede
 * entrar a ninguno.
 *
 * Para todos los demás la lista tiene un elemento —el suyo— y esta pantalla no
 * se llega a ver: el guardián de `App` solo manda aquí a quien no tiene
 * ninguno activo.
 *
 * ## Por qué se pide un token nuevo y no basta con recordar la elección
 *
 * Porque el condominio activo **viaja dentro del token**. Guardarlo en el
 * navegador y mandarlo en una cabecera dejaría que quien llama lo cambiara en
 * cada petición, que es exactamente la vulnerabilidad que todo el aislamiento
 * existe para impedir. Aquí se elige una vez, el servidor comprueba si puede, y
 * lo que vuelve es una credencial nueva.
 */
export function FraccionamientoPagina() {
  const { usuario, entrarACondominio } = useSesion();
  const navegar = useNavigate();
  const { datos, cargando, error } = useRecurso<CondominioDisponible[]>('/auth/condominios');
  const [entrando, setEntrando] = useState<string | null>(null);
  const [falla, setFalla] = useState<string | null>(null);

  // De la sesión y no del `activo` que manda el API: ese lo calcula con la
  // sesión del momento en que se preguntó, y al cambiar de fraccionamiento sin
  // salir de esta pantalla se quedaría señalando el anterior.
  const activo = usuario?.condominios[0] ?? null;

  async function entrar(c: CondominioDisponible) {
    setFalla(null);
    setEntrando(c.id);
    try {
      await entrarACondominio(c.id);
      navegar('/', { replace: true });
    } catch (e) {
      setFalla(e instanceof ErrorApi ? e.message : 'No se pudo entrar a ese fraccionamiento');
      setEntrando(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <h1 className="text-headline-sm text-on-surface">Elige un fraccionamiento</h1>
      <p className="mt-1 text-body-md text-on-surface-variant">
        {activo
          ? 'Estás trabajando en uno. Cambiar de fraccionamiento cambia todo lo que ves.'
          : 'Tu cuenta administra la plataforma y no pertenece a ninguno. Entra a uno para operar su padrón.'}
      </p>

      {falla && <ErrorCaja>{falla}</ErrorCaja>}
      {error && <ErrorCaja>{error}</ErrorCaja>}
      {cargando && <Cargando />}

      {datos && datos.length === 0 && (
        <Vacio>
          No hay fraccionamientos dados de alta todavía. Créalo desde la administración de la
          plataforma.
        </Vacio>
      )}

      <ul className="mt-5 flex flex-col gap-2">
        {datos?.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              disabled={entrando !== null}
              onClick={() => void entrar(c)}
              className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition
                ${
                  c.id === activo
                    ? 'border-primary bg-primary-container/30'
                    : 'border-outline-variant hover:bg-surface-variant/40'
                }
                disabled:opacity-60`}
            >
              <Iconos.unidades className="size-5 shrink-0 text-on-surface-variant" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-title-sm text-on-surface">{c.nombre}</span>
                <span className="block truncate text-body-sm text-on-surface-variant">
                  {c.slug}
                </span>
              </span>
              {c.id === activo && <span className="text-label-md text-primary">Aquí estás</span>}
              {entrando === c.id && (
                <span className="text-label-md text-on-surface-variant">Entrando…</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {activo && (
        <div className="mt-6">
          <Boton variante="texto" onClick={() => navegar(-1)}>
            Volver
          </Boton>
        </div>
      )}
    </div>
  );
}
